/**
 * AI usage: adoption, sessions, prompts, tokens and cost — the bill and the adoption curve.
 *
 * By model, by tool and by day for everyone; by person only when an administrator turned that on,
 * and then alphabetical, with nothing about output. Spending more tokens is not doing more work,
 * and this page says so where the numbers are.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import { api } from '../lib/api'
import { atLeast, getSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { formatCompact, formatCost, formatNumber, formatRelative } from '../lib/format'
import { Badge, EmptyState, Panel, Segmented, Skeleton, Stat } from '../components/primitives'
import type { UsageSlice } from '../lib/types'
import { SpendLimits } from '../components/SpendLimits'

type Period = '7d' | '30d' | '90d'
const DAYS: Record<Period, number> = { '7d': 7, '30d': 30, '90d': 90 }

export default function Usage() {
  const { t, locale } = useI18n()
  const isAdmin = atLeast(getSession()?.role, 'Admin')
  const [period, setPeriod] = useState<Period>('30d')
  const [teamId, setTeamId] = useState('')
  const [includeSimulated, setIncludeSimulated] = useState(false)

  const { data: teams } = useQuery({ queryKey: ['teams'], queryFn: api.settings.teams, staleTime: 60_000 })
  const { data, isLoading } = useQuery({
    queryKey: ['usage', period, teamId, includeSimulated],
    queryFn: () => api.usage.get({
      from: new Date(Date.now() - DAYS[period] * 86_400_000).toISOString(),
      teamId: teamId || undefined,
      includeSimulated,
    }),
  })

  const adoption = data && data.totalDevelopers > 0 ? Math.round((data.activeDevelopers / data.totalDevelopers) * 100) : 0
  const maxDay = Math.max(1, ...(data?.byDay ?? []).map((d) => d.tokens))

  return (
    <div className="stack stack--loose">
      <div className="row row--wrap" style={{ gap: 'var(--s3)' }}>
        <Segmented<Period> value={period} options={(['7d', '30d', '90d'] as const).map((p) => ({ value: p, label: t(`usage.period.${p}`) }))} onChange={setPeriod} />
        <select className="select select--sm" value={teamId} onChange={(e) => setTeamId(e.target.value)} style={{ width: 'auto' }} aria-label={t('reports.team')}>
          <option value="">{t('teamPicker.all')}</option>
          {(teams ?? []).map((team) => <option key={team.id} value={team.id}>{team.kind === 'Squad' ? `↳ ${team.name}` : team.name}</option>)}
        </select>
        <label className="checkbox">
          <input type="checkbox" checked={includeSimulated} onChange={(e) => setIncludeSimulated(e.target.checked)} />
          <span className="checkbox__text small">{t('reports.includeSimulated')}</span>
        </label>
      </div>

      <p className="row row--tight tiny muted" style={{ alignItems: 'flex-start', margin: 0 }}>
        <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden /> {t('usage.note')}
      </p>

      {/* Warn at, stop at — per organization, project, agent or person (51, §6.3). */}
      <SpendLimits />

      {isLoading || !data ? (
        <div className="grid grid--stats">{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} height={88} />)}</div>
      ) : (
        <>
          <div className="grid grid--stats">
            <Stat label={t('usage.adoption')} value={`${adoption}%`} foot={t('usage.adoptionFoot', { active: data.activeDevelopers, total: data.totalDevelopers })} tone="accent" />
            <Stat label={t('reports.workSessions')} value={formatNumber(data.sessions, locale)} />
            <Stat label={t('reports.promptsSent')} value={formatNumber(data.prompts, locale)} />
            <Stat
              label={t('reports.tokens')}
              value={formatCompact(data.inputTokens + data.outputTokens, locale)}
              foot={t('usage.tokensFoot', { input: formatCompact(data.inputTokens, locale), output: formatCompact(data.outputTokens, locale), cached: formatCompact(data.cachedInputTokens, locale) })}
            />
            <Stat label={t('reports.cost')} value={data.costUsd == null ? '—' : formatCost(data.costUsd, locale)} foot={t(`cost.${data.costAccuracy}`)} tone="accent" />
          </div>

          <Panel title={t('usage.byDay')}>
            {data.byDay.length === 0 ? (
              <EmptyState title={t('usage.empty')} body={includeSimulated ? undefined : t('reports.noDataDemoHint')} />
            ) : (
              <div className="usage__days" role="img" aria-label={t('usage.byDay')}>
                {data.byDay.map((day) => (
                  <div key={day.date} className="usage__day" title={`${day.date} · ${t('usage.dayTip', { developers: day.developers, sessions: day.sessions, tokens: formatCompact(day.tokens, locale) })}${day.costUsd != null ? ` · ${formatCost(day.costUsd, locale)}` : ''}`}>
                    <div className="usage__bar" style={{ height: `${Math.max(3, (day.tokens / maxDay) * 100)}%` }} />
                    <span className="usage__day-label tiny muted">{day.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <div className="grid grid--two">
            <SliceTable title={t('usage.byModel')} rows={data.byModel} locale={locale} />
            <SliceTable title={t('usage.byTool')} rows={data.byProvider} locale={locale} />
          </div>

          {data.perPersonEnabled && data.perPerson ? (
            <Panel title={t('usage.perPerson')} subtitle={t('usage.perPersonHint')} flush>
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('usage.person')}</th>
                      <th className="right">{t('usage.daysActive')}</th>
                      <th className="right">{t('reports.workSessions')}</th>
                      <th className="right">{t('reports.promptsSent')}</th>
                      <th className="right">{t('reports.tokens')}</th>
                      <th className="right">{t('reports.cost')}</th>
                      <th>{t('usage.models')}</th>
                      <th>{t('usage.lastActive')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.perPerson.map((person) => (
                      <tr key={person.developerId}>
                        <td>
                          <Link to={`/developers/${person.developerId}/day`} className="small bold">{person.name}</Link>
                          {person.team && <div className="tiny muted">{person.team}</div>}
                        </td>
                        <td className="right small">{person.daysActive}</td>
                        <td className="right small">{person.sessions}</td>
                        <td className="right small">{person.prompts}</td>
                        <td className="right small">{formatCompact(person.tokens, locale)}</td>
                        <td className="right small">{person.costUsd == null ? '—' : formatCost(person.costUsd, locale)}</td>
                        <td>
                          <span className="row row--wrap" style={{ gap: 4 }}>
                            {person.models.map((model) => <Badge key={model}>{model}</Badge>)}
                            {person.providers.map((provider) => <span key={provider} className="tiny muted">{provider}</span>)}
                          </span>
                        </td>
                        <td className="tiny muted">{person.lastActiveAt ? formatRelative(person.lastActiveAt, locale) : t('usage.never')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          ) : (
            <Panel title={t('usage.perPerson')}>
              <p className="small muted" style={{ margin: 0 }}>
                {t('usage.perPersonOff')}{' '}
                {isAdmin && <Link to="/settings/telemetry">{t('usage.perPersonTurnOn')}</Link>}
              </p>
            </Panel>
          )}
        </>
      )}
    </div>
  )
}

function SliceTable({ title, rows, locale }: { title: string; rows: UsageSlice[]; locale: Parameters<typeof formatNumber>[1] }) {
  const { t } = useI18n()
  if (rows.length === 0) return null

  return (
    <Panel title={title} flush>
      <table className="table">
        <thead>
          <tr>
            <th>{title}</th>
            <th className="right">{t('usage.people')}</th>
            <th className="right">{t('reports.workSessions')}</th>
            <th className="right">{t('reports.tokens')}</th>
            <th className="right">{t('reports.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="small">{row.label === 'unknown' ? t('usage.unknownModel') : row.label}</td>
              <td className="right small">{row.developers}</td>
              <td className="right small">{row.sessions}</td>
              <td className="right small">{formatCompact(row.tokens, locale)}</td>
              <td className="right small">{row.costUsd == null ? '—' : formatCost(row.costUsd, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )
}
